import { faClose, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';

interface SearchBoxProps {
  searchString?: string;
  placeholder?: string;
  autoSubmit?: boolean;
  onSearch: (searchString: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * A responsive search component that collapses to a button on mobile
 * and expands to a full search field when clicked.
 */
const SearchBox: React.FC<SearchBoxProps> = ({
  searchString,
  onSearch,
  placeholder = 'Search...',
  autoSubmit = true,
  onOpen,
  onClose,
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [inputValue, setInputValue] = useState(searchString);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (autoSubmit) {
      onSearch(value);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(inputValue);
  };

  const handleClear = () => {
    setInputValue('');
    onSearch('');
    handleClose();
  };

  const handleOpen = () => {
    setIsSearchOpen(true);
    onOpen?.();
  };

  const handleClose = () => {
    setIsSearchOpen(false);
    onClose?.();
  };

  return (
    <div className="flex justify-end items-center">
      <div
        className={`bg-gray-50 rounded-lg border border-gray-200 border-solid px-3 py-2
          ${isSearchOpen ? 'max-lg:hidden' : ''}
          lg:hidden
        `}
        onClick={handleOpen}
      >
        <FontAwesomeIcon icon={faSearch} color={'grey'} />
      </div>
      <form
        onSubmit={handleSubmit}
        className={`
          flex items-center
          max-lg:flex-1
          ${isSearchOpen ? '' : 'max-lg:hidden'}
        `}
      >
        <input
          key={isSearchOpen ? 'open' : 'closed'}
          type="search"
          placeholder={placeholder}
          className={`p-2.5 ${!autoSubmit ? 'rounded-l-lg' : 'rounded-lg'}
            max-lg:w-full hide-search-controls-on-mobile overflow-hidden border border-gray-200 focus:border-gray-600 focus:outline-none focus:ring-0
            sm:w-80 
          `}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setInputValue(searchString);
              onSearch('');
              handleClose();
            }
          }}
          autoFocus={isSearchOpen}
        />
        {!autoSubmit && (
          <button
            type="submit"
            className="px-3 py-2.5 bg-blue-500 text-white rounded-r-lg hover:bg-blue-600"
            aria-label="Search"
          >
            <FontAwesomeIcon icon={faSearch} />
          </button>
        )}
      </form>
      <div
        className={`px-3 py-2
          lg:hidden
          ${isSearchOpen ? '' : 'max-lg:hidden'}
        `}
        onClick={handleClear}
      >
        <FontAwesomeIcon icon={faClose} color={'grey'} />
      </div>
    </div>
  );
};

export default SearchBox;
